import React, { useState, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';
import UserDetailModal from './UserDetailModal';
import AgentDefaultTemplateModal from './AgentDefaultTemplateModal';

export default function AgentAdminSection({ currentUser }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showApplyAll, setShowApplyAll] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getAccounts();
      setAccounts(data.users || []);
    } catch (err) {
      console.error('Failed to load agents:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const agents = accounts.filter(u => u.role === 'agent');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter(a =>
      String(a.name || '').toLowerCase().includes(q) ||
      String(a.username || '').toLowerCase().includes(q));
  }, [agents, search]);

  if (loading) {
    return <p style={{ color: colors.textMuted, fontFamily }}>Loading agents...</p>;
  }

  return (
    <div style={{ color: colors.textPrimary, fontFamily }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ color: colors.textPrimary, margin: 0 }}>Agent Admin ({search ? `${filtered.length} / ` : ''}{agents.length})</h2>
        {agents.length > 0 && (
          <button
            onClick={() => setShowApplyAll(true)}
            style={{ background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 6, padding: '0.4rem 0.9rem', cursor: 'pointer', fontWeight: 700, fontFamily, fontSize: '0.85rem' }}
          >
            Apply to All
          </button>
        )}
      </div>

      {agents.length > 0 && (
        <div style={{ position: 'relative', marginBottom: '1rem', maxWidth: 360 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: colors.textMuted }} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or username..."
            style={{ width: '100%', padding: '0.5rem 0.75rem 0.5rem 2rem', background: colors.surfaceBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 6, color: colors.textPrimary, fontFamily, fontSize: '0.85rem', boxSizing: 'border-box' }}
          />
        </div>
      )}

      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {filtered.length === 0 && (
          <p style={{ color: colors.textMuted }}>No agents match "{search}".</p>
        )}
        {filtered.map(a => (
          <button
            key={a.id}
            onClick={() => setSelected(a)}
            style={{ textAlign: 'left', background: colors.cardBg, boxShadow: colors.shadowCard, border: 'none', borderRadius: 8, padding: '0.75rem 1rem', color: colors.textPrimary, cursor: 'pointer', fontFamily }}
          >
            <span style={{ fontWeight: 600 }}>{a.name}</span>
            <span style={{ color: colors.textMuted, marginLeft: '0.5rem' }}>@{a.username}</span>
            {!a.is_active && (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', padding: '2px 8px', borderRadius: 20, background: 'rgba(220,53,69,0.2)', color: colors.negative }}>Disabled</span>
            )}
            {a.is_active && a.is_read_only && (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', padding: '2px 8px', borderRadius: 20, background: 'rgba(249,115,22,0.2)', color: colors.pending }}>Read-Only</span>
            )}
          </button>
        ))}
      </div>

      {selected && (
        <UserDetailModal
          user={selected}
          currentUser={currentUser}
          accounts={accounts}
          onClose={() => setSelected(null)}
          onUpdate={load}
        />
      )}

      {showApplyAll && (
        <AgentDefaultTemplateModal
          agentCount={agents.length}
          onClose={() => setShowApplyAll(false)}
          onApplied={load}
        />
      )}
    </div>
  );
}
